import React, { useState, useEffect } from 'react';
import { theme } from '../theme';
import Icons from './Icons';
import { apiPost } from '../api';
import { Spinner, Badge } from './ui';
import { useConfig } from '../context/ConfigContext';

// ---------------------------------------------------------------------------
// DB Browser — schema tree (sizing/drag handled by parent RightPanel)
// ---------------------------------------------------------------------------
export default function DBBrowser({ schema, loading, onRefresh, onClose, onTableAction }) {
  const { config } = useConfig();
  const [search, setSearch] = useState('');
  const [expandedOwners, setExpandedOwners] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const [tableColumns, setTableColumns] = useState({}); // { "OWNER.TABLE": [columns] }
  const [loadingColumns, setLoadingColumns] = useState({}); // { "OWNER.TABLE": true }
  const [copiedItem, setCopiedItem] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, owner, table }

  const toggleOwner = (owner) => {
    setExpandedOwners((prev) => ({ ...prev, [owner]: !prev[owner] }));
  };

  // Toggle table + lazy-load columns
  const toggleTable = async (owner, tableName) => {
    const key = `${owner}.${tableName}`;
    const isExpanded = expandedTables[key];
    setExpandedTables((prev) => ({ ...prev, [key]: !isExpanded }));

    // Fetch columns if expanding and not yet loaded
    if (!isExpanded && !tableColumns[key] && !loadingColumns[key]) {
      setLoadingColumns((prev) => ({ ...prev, [key]: true }));
      try {
        const result = await apiPost('/bip/schema/columns', { owner, table: tableName, ...config });
        if (result.columns) {
          setTableColumns((prev) => ({ ...prev, [key]: result.columns }));
        }
      } catch (err) {
        console.error('Failed to load columns:', err);
      }
      setLoadingColumns((prev) => ({ ...prev, [key]: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(text);
      setTimeout(() => setCopiedItem(null), 1500);
    });
  };

  // Filter schema by table name search
  const filteredSchema = search.trim()
    ? schema.map((db) => ({
        ...db,
        tables: db.tables.filter((t) => t.name.toUpperCase().includes(search.toUpperCase())),
      })).filter((db) => db.tables.length > 0)
    : schema;

  // Auto-expand owners when searching
  useEffect(() => {
    if (search.trim() && filteredSchema.length > 0) {
      const owners = {};
      filteredSchema.forEach((db) => { owners[db.owner] = true; });
      setExpandedOwners(owners);
    }
  }, [search]); // filteredSchema is derived from schema+search; re-running only on search change is intentional

  useEffect(() => {
    if (!contextMenu) return;
    const dismiss = () => setContextMenu(null);
    document.addEventListener('click', dismiss);
    return () => document.removeEventListener('click', dismiss);
  }, [contextMenu]);

  const totalTables = schema.reduce((acc, db) => acc + db.tables.length, 0);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{
          padding: '10px 12px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Database />
            <span style={{ fontWeight: 600, fontSize: '13px' }}>DB Browser</span>
            {totalTables > 0 && <Badge>{totalTables}</Badge>}
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={onRefresh} disabled={loading}
              style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
              title="Refresh schema"
            >
              <Icons.Refresh />
            </button>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
              title="Close panel"
            >
              <Icons.X />
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}` }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tables..."
            style={{
              width: '100%', padding: '6px 10px', background: theme.codeBg,
              border: `1px solid ${theme.border}`, borderRadius: '6px',
              color: theme.text, fontSize: '12px', outline: 'none',
            }}
            onFocus={(e) => (e.target.style.borderColor = theme.borderFocus)}
            onBlur={(e) => (e.target.style.borderColor = theme.border)}
          />
        </div>

        {/* Tree content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '4px 0' }}>
          {loading && (
            <div style={{ padding: '24px', textAlign: 'center', color: theme.textMuted }}>
              <Spinner />
              <p style={{ marginTop: '8px', fontSize: '12px' }}>Loading tables...</p>
            </div>
          )}

          {!loading && filteredSchema.length === 0 && schema.length === 0 && (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: theme.textMuted, fontSize: '12px' }}>
              <Icons.Database />
              <p style={{ marginTop: '8px' }}>No schema loaded yet.</p>
              <p style={{ marginTop: '4px' }}>Click refresh to load.</p>
            </div>
          )}

          {!loading && filteredSchema.length === 0 && schema.length > 0 && (
            <div style={{ padding: '16px 12px', textAlign: 'center', color: theme.textMuted, fontSize: '12px' }}>
              No matches for "{search}"
            </div>
          )}

          {filteredSchema.map((db) => (
            <div key={db.owner}>
              {/* Owner node */}
              <div
                onClick={() => toggleOwner(db.owner)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
                  cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: theme.text,
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = theme.surfaceHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {expandedOwners[db.owner] ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                <Icons.Database />
                <span style={{ flex: 1 }}>{db.owner}</span>
                <span style={{ color: theme.textMuted, fontWeight: 400, fontSize: '11px' }}>{db.tables.length}</span>
              </div>

              {/* Tables */}
              {expandedOwners[db.owner] && db.tables.map((table) => {
                const tableKey = `${db.owner}.${table.name}`;
                const cols = tableColumns[tableKey];
                const colsLoading = loadingColumns[tableKey];
                const isExpanded = expandedTables[tableKey];

                return (
                  <div key={tableKey}>
                    {/* Table node */}
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '4px 10px 4px 24px', cursor: 'pointer',
                        fontSize: '12px', color: theme.text, userSelect: 'none',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = theme.surfaceHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({ x: e.clientX, y: e.clientY, owner: db.owner, table: table.name });
                      }}
                    >
                      <span onClick={() => toggleTable(db.owner, table.name)} style={{ display: 'flex', alignItems: 'center' }}>
                        {colsLoading
                          ? <div style={{ width: 14, height: 14, border: `2px solid ${theme.border}`, borderTopColor: theme.accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          : isExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />
                        }
                      </span>
                      <span onClick={() => toggleTable(db.owner, table.name)} style={{ display: 'flex', alignItems: 'center' }}>
                        <Icons.Table />
                      </span>
                      <span onClick={() => toggleTable(db.owner, table.name)} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {table.name}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(table.name); }}
                        style={{
                          background: 'none', border: 'none', padding: '2px',
                          color: copiedItem === table.name ? theme.success : theme.textMuted,
                          cursor: 'pointer', opacity: copiedItem === table.name ? 1 : 0.4,
                          transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = copiedItem === table.name ? '1' : '0.4')}
                        title="Copy table name"
                      >
                        {copiedItem === table.name ? <Icons.Check /> : <Icons.Copy />}
                      </button>
                      {cols && <span style={{ color: theme.textMuted, fontSize: '11px' }}>{cols.length}</span>}
                    </div>

                    {/* Columns — lazy loaded */}
                    {isExpanded && cols && cols.map((col) => (
                      <div
                        key={`${tableKey}.${col.name}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '3px 10px 3px 52px', cursor: 'pointer',
                          fontSize: '11px', color: theme.textMuted, userSelect: 'none',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = theme.surfaceHover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <Icons.Column />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {col.name}
                        </span>
                        <span style={{
                          fontSize: '10px', padding: '1px 5px', borderRadius: '3px',
                          background: `${theme.accent}15`, color: theme.accent,
                          fontFamily: 'monospace', flexShrink: 0,
                        }}>
                          {col.type}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(col.name); }}
                          style={{
                            background: 'none', border: 'none', padding: '2px',
                            color: copiedItem === col.name ? theme.success : theme.textMuted,
                            cursor: 'pointer', opacity: copiedItem === col.name ? 1 : 0.3,
                            transition: 'opacity 0.15s', flexShrink: 0,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = copiedItem === col.name ? '1' : '0.3')}
                          title="Copy column name"
                        >
                          {copiedItem === col.name ? <Icons.Check /> : <Icons.Copy />}
                        </button>
                      </div>
                    ))}

                    {/* Loading indicator for columns */}
                    {isExpanded && colsLoading && (
                      <div style={{ padding: '6px 52px', fontSize: '11px', color: theme.textMuted }}>
                        Loading columns...
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 1000,
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            minWidth: '180px',
            padding: '4px 0',
          }}
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={() => {
              const alias = contextMenu.table.split('_').map(w => w[0]).join('').toLowerCase();
              const sql = `SELECT *\nFROM   ${contextMenu.owner}.${contextMenu.table} ${alias}\nWHERE  ROWNUM <= 100`;
              onTableAction?.(sql, contextMenu.table);
              setContextMenu(null);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 14px', background: 'none', border: 'none',
              color: theme.text, fontSize: '13px', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            Select Top 100
          </button>
        </div>
      )}
    </div>
  );
}
